import React from 'react';
import { Spinner } from '@components/spinner';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Button } from '@components/ui/button';
import { Checkbox } from '@components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@components/ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '@components/ui/table';
import { Api } from '../../core/api';
import {
    ProductOrderState,
    ThirdPartyPurchaseSupportDto,
    VendorSource
} from '../../core/api/WalletClient';
import { injectTSDI } from '../../core/tsdi';

const BULK_SYNC_LIMIT = 500;
const BULK_SYNC_CONCURRENCY = 5;

interface BulkSyncModalProps {
    open: boolean;
    sourceFilter: VendorSource | 'ALL';
    search?: string;
    onClose(): void;
    onSuccess(): void;
}

interface BulkSyncResult {
    total: number;
    processed: number;
    success: number;
    failed: number;
    failures: Array<{ purchaseId: number; message: string }>;
}

function getErrorMessage(error: any): string {
    if (error && typeof error === 'object' && 'error' in error) {
        const errorObject = error.error;
        if (errorObject?.message?.defaultMessage) {
            return errorObject.message.defaultMessage;
        }
        if (errorObject?.defaultMessage) {
            return errorObject.defaultMessage;
        }
    }
    if (error instanceof Error) {
        return error.message;
    }
    return 'Failed to sync purchase';
}

export const BulkSyncModal = ({
    open,
    sourceFilter,
    search,
    onClose,
    onSuccess
}: BulkSyncModalProps) => {
    const { walletClient } = injectTSDI(Api);
    const [loading, setLoading] = React.useState(false);
    const [running, setRunning] = React.useState(false);
    const [items, setItems] = React.useState<ThirdPartyPurchaseSupportDto[]>(
        []
    );
    const [totalElements, setTotalElements] = React.useState(0);
    const [pendingTotalElements, setPendingTotalElements] = React.useState(0);
    const [selectedIds, setSelectedIds] = React.useState<Set<number>>(
        new Set<number>()
    );
    const [result, setResult] = React.useState<BulkSyncResult | null>(null);
    const [error, setError] = React.useState<string | null>(null);

    const overLimit = totalElements > BULK_SYNC_LIMIT;
    const selectedCount = selectedIds.size;
    const excludedPendingCount = Math.max(
        pendingTotalElements - totalElements,
        0
    );

    const loadSyncablePurchases = React.useCallback(async () => {
        setLoading(true);
        setError(null);
        setResult(null);
        try {
            const query = {
                statuses: [ProductOrderState.PENDING],
                page: 0,
                sort: '-createdDate',
                ...(sourceFilter !== 'ALL' && {
                    sources: [sourceFilter]
                }),
                ...(search &&
                    search.trim() && {
                        search: search.trim()
                    })
            };

            const [syncableResponse, pendingResponse] = await Promise.all([
                walletClient.supportApi.purchaseSupportControllerGetThirdPartyPurchases(
                    {
                        ...query,
                        syncableOnly: true,
                        size: BULK_SYNC_LIMIT
                    }
                ),
                walletClient.supportApi.purchaseSupportControllerGetThirdPartyPurchases(
                    {
                        ...query,
                        size: 1
                    }
                )
            ]);

            const syncableData = syncableResponse.data;
            const pendingData = pendingResponse.data;
            const content = syncableData.content || [];
            setItems(content);
            setTotalElements(syncableData.totalElements || 0);
            setPendingTotalElements(pendingData.totalElements || 0);
            setSelectedIds(new Set(content.map((item) => item.purchaseId)));
        } catch (e: any) {
            setError(getErrorMessage(e));
            setItems([]);
            setTotalElements(0);
            setPendingTotalElements(0);
            setSelectedIds(new Set<number>());
        } finally {
            setLoading(false);
        }
    }, [walletClient, sourceFilter, search]);

    React.useEffect(() => {
        if (!open) {
            return;
        }
        void loadSyncablePurchases();
    }, [open, loadSyncablePurchases]);

    const toggleSelection = (purchaseId: number) => {
        setSelectedIds((current) => {
            const next = new Set(current);
            if (next.has(purchaseId)) {
                next.delete(purchaseId);
            } else {
                next.add(purchaseId);
            }
            return next;
        });
    };

    const handleSelectAll = () => {
        setSelectedIds(new Set(items.map((item) => item.purchaseId)));
    };

    const handleDeselectAll = () => {
        setSelectedIds(new Set<number>());
    };

    const runSync = async () => {
        const selectedItems = items.filter((item) =>
            selectedIds.has(item.purchaseId)
        );
        if (selectedItems.length === 0) {
            return;
        }

        setRunning(true);
        setError(null);
        setResult({
            total: selectedItems.length,
            processed: 0,
            success: 0,
            failed: 0,
            failures: []
        });

        const queue = [...selectedItems];

        const workers = Array.from(
            { length: Math.min(BULK_SYNC_CONCURRENCY, queue.length) },
            async () => {
                while (queue.length > 0) {
                    const item = queue.shift();
                    if (!item) {
                        break;
                    }

                    let failureMessage: string | null = null;
                    try {
                        await walletClient.supportApi.purchaseSupportControllerSyncPurchase(
                            item.purchaseId
                        );
                    } catch (e: any) {
                        failureMessage = getErrorMessage(e);
                    }

                    setResult((current) => {
                        if (!current) {
                            return current;
                        }
                        return {
                            ...current,
                            processed: current.processed + 1,
                            success: current.success + (failureMessage ? 0 : 1),
                            failed: current.failed + (failureMessage ? 1 : 0),
                            failures: failureMessage
                                ? [
                                      ...current.failures,
                                      {
                                          purchaseId: item.purchaseId,
                                          message: failureMessage
                                      }
                                  ]
                                : current.failures
                        };
                    });
                }
            }
        );

        await Promise.all(workers);
        setRunning(false);
        onSuccess();
    };

    const handleClose = () => {
        if (running) {
            return;
        }
        onClose();
    };

    return (
        <Dialog open={open} onOpenChange={(next) => !next && handleClose()}>
            <DialogContent className="sm:max-w-3xl">
                <DialogHeader>
                    <DialogTitle>Re-sync Pending Voucher Orders</DialogTitle>
                </DialogHeader>

                <div className="flex flex-col gap-4">
                    {loading && (
                        <div className="flex justify-center py-6">
                            <Spinner />
                        </div>
                    )}

                    {error && (
                        <Alert variant="destructive">
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}

                    {!loading && !error && overLimit && (
                        <Alert variant="warning">
                            <AlertDescription>
                                Current filters returned {totalElements}{' '}
                                syncable purchases. Maximum allowed in one run
                                is {BULK_SYNC_LIMIT}. Narrow filters and try
                                again.
                            </AlertDescription>
                        </Alert>
                    )}

                    {!loading && !error && !overLimit && items.length === 0 && (
                        <Alert variant="info">
                            <AlertDescription>
                                No syncable pending purchases for current
                                filters.
                            </AlertDescription>
                        </Alert>
                    )}

                    {!loading &&
                        !error &&
                        !overLimit &&
                        excludedPendingCount > 0 && (
                            <Alert variant="info">
                                <AlertDescription>
                                    Excluded {excludedPendingCount} pending
                                    purchases because they cannot be synced.
                                </AlertDescription>
                            </Alert>
                        )}

                    {!loading && !error && !overLimit && items.length > 0 && (
                        <>
                            <div className="flex items-center justify-between">
                                <p className="text-sm">
                                    Selected {selectedCount} of {items.length}{' '}
                                    syncable purchases
                                </p>
                                <div className="flex items-center gap-1">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={handleSelectAll}
                                        disabled={running}
                                    >
                                        Select all
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={handleDeselectAll}
                                        disabled={running}
                                    >
                                        Deselect all
                                    </Button>
                                </div>
                            </div>

                            <div className="max-h-[360px] overflow-auto rounded-md border">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="w-12" />
                                            <TableHead>Purchase ID</TableHead>
                                            <TableHead>User ID</TableHead>
                                            <TableHead>Vendor</TableHead>
                                            <TableHead>Order ID</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {items.map((item) => (
                                            <TableRow
                                                key={item.purchaseId}
                                                className="cursor-pointer"
                                                onClick={() =>
                                                    toggleSelection(
                                                        item.purchaseId
                                                    )
                                                }
                                            >
                                                <TableCell>
                                                    <Checkbox
                                                        checked={selectedIds.has(
                                                            item.purchaseId
                                                        )}
                                                        onCheckedChange={() =>
                                                            toggleSelection(
                                                                item.purchaseId
                                                            )
                                                        }
                                                        onClick={(event) =>
                                                            event.stopPropagation()
                                                        }
                                                        disabled={running}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {item.purchaseId}
                                                </TableCell>
                                                <TableCell>
                                                    {item.userId}
                                                </TableCell>
                                                <TableCell>
                                                    {item.vendorName} (
                                                    {item.vendorSource})
                                                </TableCell>
                                                <TableCell>
                                                    {item.orderId}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </>
                    )}

                    {result && (
                        <Alert
                            variant={result.failed > 0 ? 'warning' : 'success'}
                        >
                            <AlertDescription>
                                Processed {result.processed}/{result.total}.
                                Success: {result.success}, Failed:{' '}
                                {result.failed}
                            </AlertDescription>
                        </Alert>
                    )}

                    {result && result.failures.length > 0 && (
                        <div className="max-h-[220px] overflow-auto rounded-md border">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Purchase ID</TableHead>
                                        <TableHead>Error</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {result.failures.map((failure) => (
                                        <TableRow key={failure.purchaseId}>
                                            <TableCell>
                                                {failure.purchaseId}
                                            </TableCell>
                                            <TableCell>
                                                {failure.message}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button
                        variant="ghost"
                        onClick={handleClose}
                        disabled={running}
                    >
                        Close
                    </Button>
                    <Button
                        disabled={
                            loading ||
                            overLimit ||
                            items.length === 0 ||
                            selectedCount === 0
                        }
                        loading={running}
                        onClick={() => void runSync()}
                    >
                        {running ? 'Syncing...' : 'Re-sync selected'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
